"use client"

import React, {useEffect, useLayoutEffect, useMemo, useRef} from "react";
import {motion, useAnimationControls, useReducedMotion} from "framer-motion";
import {cn} from "@/lib/utils";
import Image from "next/image";
import CursorBubble from "@/components/global/ui/CursorBubble";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import grid from "@/public/animatedAssets/grid.svg";

type State = "collapsed" | "expanded";

export const AnimatedAsset3 = ({
  className = "",
}: {
  className?: string;
}) => {
  const controls = useAnimationControls();
  const prefersReduced = useReducedMotion();
  const stateRef = useRef<State>("collapsed");
  const rootRef = useRef<HTMLDivElement | null>(null);

  const yLabels = useMemo(() => {
    const range = { min: 1.245, max: 1.680, step: 0.015, decimals: 3 };
    return Array.from(
      { length: Math.round((range.max - range.min) / range.step) + 1 },
      (_, i) => (range.min + i * range.step).toFixed(range.decimals)
    ).reverse();
  }, []);

  const middleIndex = Math.floor(yLabels.length / 2);

  const setState = (next: State) => {
    if (stateRef.current === next) return Promise.resolve();
    stateRef.current = next;
    return controls.start(next);
  };

  useEffect(() => {
    controls.set("collapsed");
    stateRef.current = "collapsed";
  }, [controls]);

  useLayoutEffect(() => {
    if (prefersReduced) return;
    const el = rootRef.current;
    if (!el) return;

    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    if (isTouchDevice) {
      const st = ScrollTrigger.create({
        trigger: el,
        start: "top 50%",
        end: "bottom 50%",
        onEnter: () => setState("expanded"),
        onLeave: () => {},
        onEnterBack: () => setState("expanded"),
        onLeaveBack: () => setState("collapsed"),
        invalidateOnRefresh: true,
      });

      return () => st.kill();
    }

    return;
  }, [prefersReduced, controls]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMouseEnter = async () => {
    if (prefersReduced) return;
    await setState("expanded");
  };

  const handleMouseLeave = () => {
    if (prefersReduced) return;
    setState("collapsed");
  };

  const ease = {ease: "circOut", duration: .75};

  const gridVariants = {
    collapsed: {opacity: .03, width: "243.25rem", transition: ease},
    expanded: {opacity: .07, width: "163.75rem", transition: ease},
  };

  const graphVariants = {
    collapsed: {y: "15.625rem", opacity: .7, width: "65.25rem", height: "39.375rem", transition: ease},
    expanded: {y: "25.875rem", opacity: 1, width: "43.875rem", height: "10rem", transition: ease},
  };

  const squareVariants = {
    collapsed: {width: 0, height: 0, opacity: 0, transition: ease},
    expanded: {width: "100%", height: "100%", opacity: 1, transition: ease},
  }

  const cursorVariants = {
    collapsed: {top: "calc(100% + 2rem)", y: 0, x: 0, left: "3rem", transition: ease},
    expanded: {top: "0", y: "1.25rem", x: "2.5rem", left: "100%", transition: ease},
  }

  const yAxisVariants = {
    collapsed: {y: 0, transition: ease},
    expanded: {y: "-70%", transition: ease},
  }

  const yLabelPointer = {
    collapsed: {opacity: 0, transition: ease},
    expanded: {opacity: 1, transition: ease},
  }

  const yLabelsVariants = {
    collapsed: {opacity: .15, transition: ease},
    expanded: {opacity: .4, transition: ease},
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        "w-full h-[55rem] lg:h-full bg-[#0A1A1D] px-[2rem] sm:px-[3rem] relative overflow-clip flex gap-[1rem]",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[243.25rem] opacity-[.03] pointer-events-none"
        variants={gridVariants}
        animate={controls}
        initial="collapsed"
      >
        <Image
          className="!w-full !h-full !max-w-[unset]"
          src={grid}
          alt=""
        />
      </motion.div>

      <div className="grow w-[37.625rem] relative overflow-clip">
        <motion.div
          className="relative"
          variants={graphVariants}
          animate={controls}
          initial="collapsed"
        >
          <svg className="!w-full !h-full" preserveAspectRatio="none" viewBox="0 0 524 316" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.391602 15.5L2.7791 12.5L5.1666 33.5L7.5541 30.5L9.9416 3.5L12.3291 0.5L14.7166 84.5L17.1041 93.5L19.4916 96.5L21.8791 93.5L24.2666 96.5L26.6541 99.5L29.0416 90.5L31.4291 123.5L33.8166 129.5L36.2041 126.5L38.5916 165.5L40.9791 201.5L43.3666 129.5L45.7541 111.5L48.1416 147.5L50.5291 129.5L52.9166 63.5L55.3041 27.5L57.6916 30.5L60.0791 15.5L62.4666 51.5L64.8541 0.5L67.2416 30.5L69.6291 0.5L72.0166 3.5L74.4041 27.5L76.7916 30.5L79.1791 72.5L81.5666 141.5L83.9541 150.5L86.3416 153.5L88.7291 150.5L91.1166 177.5L93.5041 174.5L95.8916 192.5L98.2791 144.5L100.667 189.5L103.054 231.5L105.442 234.5L107.829 198.5L110.217 195.5L112.604 192.5L114.992 171.5L117.379 135.5L119.767 120.5L122.154 129.5L124.542 99.5L126.929 105.5H129.317L131.704 93.5L134.092 114.5L136.479 111.5L138.867 165.5L141.254 180.5L143.642 183.5L146.029 201.5L148.417 204.5L150.804 201.5L153.192 159.5L155.579 171.5L157.967 204.5L160.354 201.5L162.742 204.5L165.129 216.5L167.517 219.5L169.904 216.5L172.292 192.5L174.679 189.5H177.067L179.454 198.5L181.842 201.5L184.229 186.5L186.617 243.5L189.004 183.5L191.392 186.5L193.779 183.5L196.167 219.5L198.554 216.5L200.942 219.5L203.329 216.5L205.717 219.5L208.104 198.5L210.492 165.5L212.879 210.5L215.267 156.5L217.654 105.5L220.042 108.5L222.429 102.5L224.817 153.5L227.204 186.5L229.592 189.5L231.979 186.5L234.367 189.5L236.754 132.5L239.142 81.5L241.529 51.5L243.917 189.5L246.304 177.5L248.692 180.5L251.079 192.5L253.467 195.5L255.854 171.5H258.242L260.629 168.5L263.017 171.5L265.404 195.5L267.792 198.5L270.179 231.5L272.567 210.5L274.954 207.5L277.342 237.5L279.729 231.5L282.117 171.5L284.504 168.5L286.892 171.5H289.279L291.667 174.5L294.054 171.5L296.442 174.5L298.829 216.5L301.217 219.5L303.604 216.5L305.992 219.5L308.379 216.5L310.767 219.5L313.154 216.5L315.542 219.5L317.929 174.5L320.317 180.5H322.704L325.092 183.5L327.479 189.5L329.867 183.5L332.254 216.5L334.642 219.5L337.029 216.5L339.417 219.5L341.804 186.5L344.192 189.5L346.579 186.5L348.967 285.5L351.354 282.5L353.742 285.5L356.129 291.5L358.517 312.5L360.904 282.5L363.292 285.5L365.679 315.5L368.067 204.5L370.454 216.5L372.842 171.5L375.229 168.5L377.617 171.5L380.004 132.5L382.392 135.5L384.779 132.5L387.167 66.5L389.554 93.5L391.942 111.5L394.329 174.5L396.717 177.5L399.104 165.5L401.492 168.5L403.879 186.5L406.267 192.5L408.654 186.5L411.042 165.5L413.429 192.5L415.817 195.5L418.204 165.5L420.592 183.5L422.979 180.5L425.367 216.5L427.754 207.5L430.142 171.5L432.529 165.5L434.917 168.5L437.304 195.5L439.692 198.5L442.079 195.5L444.467 243.5L446.854 240.5L449.242 243.5L451.629 240.5L454.017 195.5L456.404 192.5L458.792 186.5L461.179 183.5L463.567 186.5L465.954 168.5H468.342L470.729 162.5L473.117 174.5L475.504 171.5L477.892 162.5L480.279 171.5L482.667 174.5L485.054 192.5L487.442 195.5L489.829 171.5L492.217 174.5L494.604 192.5L496.992 177.5L499.379 195.5L501.767 198.5L504.154 195.5L506.542 165.5L508.929 156.5L511.317 225.5L513.704 189.5L516.092 198.5L518.479 156.5L520.867 159.5L523.254 183.5" stroke="#F9E0B9" strokeLinejoin="round"/>
          </svg>
        </motion.div>
      </div>

      <div className="w-[5rem] h-full flex-shrink-0 relative">
        <motion.div
          className="absolute h-full flex flex-col gap-[3rem]"
          variants={yAxisVariants}
          animate={controls}
          initial="collapsed"
        >
          {yLabels.map((value, index) => {
            if (index === middleIndex) {
              return (
                <motion.div
                  key={index}
                  className="relative flex-shrink-0"
                  variants={yLabelPointer}
                  animate={controls}
                  initial="collapsed"
                >
                  <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[100rem] h-px bg-[#F9E0B9] opacity-30" />
                  <div className={cn(
                    "bg-[#F9E0B9] h-[4rem] w-[calc(100%+4rem)] flex flex-col items-center justify-center absolute top-1/2 -translate-y-1/2 -right-[1rem]",
                    "[clip-path:polygon(0_2rem,2rem_0,100%_0,100%_100%,2rem_100%,0_calc(100%-2rem))]"
                  )}/>
                  <p className="mb-0 text-[2rem] tabular-nums leading-none font-medium text-[#0A1A1D] relative">
                    {value}
                  </p>
                </motion.div>
              );
            }
            return (
              <motion.p
                key={index}
                className="mb-0 text-[2rem] text-white leading-none tabular-nums flex-shrink-0"
                variants={yLabelsVariants}
                animate={controls}
                initial="collapsed"
              >
                {value}
              </motion.p>
            );
          })}
        </motion.div>
      </div>

      <div className="absolute aspect-[182/238] w-[22.75rem] left-[5rem] top-[14.5rem]">
        <div className="relative w-full h-full">
          <motion.div
            className="absolute bottom-0 left-0  bg-[#F9E0B9]/10 border border-dashed border-[#F9E0B9]/70 opacity-0"
            variants={squareVariants}
            animate={controls}
            initial="collapsed"
          />

          <motion.div
            className="absolute top-[calc(100%+2rem)] left-[3rem]"
            variants={cursorVariants}
            animate={controls}
            initial="collapsed"
          >
            <CursorBubble
              name="Basti"
              color="#F9E0B9"
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};